import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Admin content preview.
 *
 * The admin panel opens /admin-preview?token=… for one post, blog or event.
 * After the API confirms the token we keep a "grant" for that one item in
 * sessionStorage (this browser tab only — it never touches the real login in
 * localStorage). While a grant is valid:
 *   - authGuard lets that one detail page open without a login;
 *   - the auth interceptor sends the token as X-Admin-Preview on that item's
 *     GET request, so the API also returns removed / draft content.
 * Everything else on the site still needs a normal login, so the page is
 * effectively read-only (likes, comments, etc. show the login prompt).
 */

export type AdminPreviewContentType = 'POST' | 'BLOG' | 'EVENT';

export interface AdminPreviewGrant {
  token: string;
  contentType: AdminPreviewContentType;
  contentId: string;
  path: string;
  /** epoch ms */
  expiresAt: number;
}

export const ADMIN_PREVIEW_HEADER = 'X-Admin-Preview';

const STORAGE_KEY = 'adminPreviewGrants';

/** API path segment for each content type's GET-by-id endpoint. */
const API_SEGMENT: Record<AdminPreviewContentType, string> = {
  POST: 'posts',
  BLOG: 'blogs',
  EVENT: 'events',
};

@Injectable({ providedIn: 'root' })
export class AdminPreviewService {
  private http = inject(HttpClient);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Emits the grant for the page currently open, or null. Drives the banner. */
  readonly active$ = new BehaviorSubject<AdminPreviewGrant | null>(null);

  /** POST /auth/admin-preview — validates a token from the admin panel. */
  verify(token: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}/auth/admin-preview`, { token });
  }

  saveGrant(grant: AdminPreviewGrant): void {
    const grants = this.read().filter((g) => g.contentId !== grant.contentId);
    grants.push(grant);
    this.write(grants);
  }

  /** Valid grant for this exact item, if any. */
  findGrant(contentType: AdminPreviewContentType, contentId: string | null | undefined): AdminPreviewGrant | null {
    if (!contentId) return null;
    return this.read().find((g) => g.contentType === contentType && g.contentId === contentId) ?? null;
  }

  /**
   * Token to send with an API request, if the request is the GET-by-id call
   * for an item this tab holds a grant for. Matches e.g. …/posts/<id>[?…].
   */
  tokenForRequest(method: string, url: string): string | null {
    if (method.toUpperCase() !== 'GET') return null;
    const grants = this.read();
    if (!grants.length) return null;

    const path = url.split('?')[0].replace(/\/+$/, '');
    for (const g of grants) {
      if (path.endsWith(`/${API_SEGMENT[g.contentType]}/${g.contentId}`)) return g.token;
    }
    return null;
  }

  setActive(grant: AdminPreviewGrant | null): void {
    if (this.active$.value !== grant) this.active$.next(grant);
  }

  // ── storage ──────────────────────────────────────────────────────────────

  /** Unexpired grants only (expired ones are pruned as a side effect). */
  private read(): AdminPreviewGrant[] {
    if (!this.isBrowser) return [];
    let grants: AdminPreviewGrant[] = [];
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      grants = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(grants)) grants = [];
    } catch {
      return [];
    }
    const now = Date.now();
    const valid = grants.filter((g) => g && g.token && g.contentId && g.expiresAt > now);
    if (valid.length !== grants.length) this.write(valid);
    return valid;
  }

  private write(grants: AdminPreviewGrant[]): void {
    if (!this.isBrowser) return;
    try {
      if (grants.length) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(grants));
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable — preview simply won't persist across reloads */
    }
  }
}
